import { useState, useEffect, useRef } from "react";
import { UserRound, KeyRound, Building2, MailCheck, Camera, IdCard, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { resizeImage } from "../lib/img";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { fmtDate } from "../lib/format";

const ROLE_LABEL = { admin: "Pentadbir", operator: "Pengendali", driver: "Staf", pemandu: "Pemandu" };

const EMAIL_PREFS = [
  { key: "emel_tempahan_dihantar", label: "Pengesahan tempahan dihantar", desc: "Emel pengesahan apabila tempahan anda berjaya dihantar", roles: ["driver"] },
  { key: "emel_tempahan_baharu", label: "Tempahan baharu perlu tindakan", desc: "Emel apabila staf menghantar permohonan tempahan baharu", roles: ["operator", "admin"] },
  { key: "emel_kelulusan_akhir", label: "Permintaan kelulusan akhir", desc: "Emel apabila pengendali menugaskan kenderaan dan memohon kelulusan akhir anda", roles: ["admin"] },
  { key: "emel_tempahan_diluluskan", label: "Tempahan diluluskan", desc: "Emel pengesahan apabila tempahan diluluskan", roles: ["driver", "operator", "admin"] },
  { key: "emel_tempahan_ditolak", label: "Tempahan ditolak", desc: "Emel apabila tempahan anda ditolak", roles: ["driver"] },
];

function initials(name = "") {
  return name.split(" ").map((w) => w.replace(/[^A-Za-z]/g, "")).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

export default function ProfilePage() {
  const { user, tenant, refresh } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [license, setLicense] = useState({ license_image: user?.license_image || "", license_expiry: user?.license_expiry || "" });
  const photoRef = useRef(null);
  const licenseRef = useRef(null);

  useEffect(() => { api.get("/profile/email-prefs").then((r) => setPrefs(r.data)); }, []);
  useEffect(() => { setLicense((l) => ({ ...l, license_expiry: user?.license_expiry || l.license_expiry })); }, [user]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const photo = await resizeImage(file, 400);
      const photo_thumb = await resizeImage(file, 96, 0.72);
      await api.put("/profile/photo", { photo, photo_thumb });
      toast.success("Foto profil dikemas kini");
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
    e.target.value = "";
  };

  const uploadLicense = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const img = await resizeImage(file, 900);
      setLicense((l) => ({ ...l, license_image: img }));
      await api.put("/profile/license", { license_image: img });
      toast.success("Gambar lesen memandu dimuat naik");
    } catch (err) {
      toast.error(apiError(err));
    }
    e.target.value = "";
  };

  const saveLicenseExpiry = async () => {
    try {
      await api.put("/profile/license", { license_expiry: license.license_expiry });
      toast.success("Tarikh luput lesen disimpan");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const togglePref = async (key, value) => {
    const prev = prefs;
    setPrefs({ ...prefs, [key]: value });
    try {
      await api.put("/profile/email-prefs", { prefs: { [key]: value } });
      toast.success(value ? "Notifikasi emel diaktifkan" : "Notifikasi emel dimatikan");
    } catch (err) {
      setPrefs(prev);
      toast.error(apiError(err));
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/profile", form);
      toast.success("Profil dikemas kini");
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      toast.error("Pengesahan kata laluan tidak sepadan");
      return;
    }
    setBusy(true);
    try {
      await api.put("/profile/password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Kata laluan berjaya ditukar");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="profile-page">
      <div className="flex items-center gap-4">
        <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="profile-photo-input" />
        <div className="relative">
          {user?.photo ? (
            <img src={user.photo} alt={user?.name} className="h-16 w-16 rounded-full object-cover border-2 border-[#10056E]/20" data-testid="profile-avatar-img" />
          ) : (
            <span className="h-16 w-16 rounded-full bg-[#10056E] text-white text-xl font-bold flex items-center justify-center">
              {initials(user?.name)}
            </span>
          )}
          <button type="button" onClick={() => photoRef.current?.click()} data-testid="profile-photo-btn"
            className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-white border shadow flex items-center justify-center text-[#10056E] hover:bg-slate-50">
            <Camera className="h-3.5 w-3.5" />
          </button>
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="profile-name">{user?.name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="secondary">{ROLE_LABEL[user?.role]}</Badge>
            <span className="text-sm text-muted-foreground">{user?.email}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-slate-200 shadow-sm rounded-xl">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2"><UserRound className="h-4 w-4" /> Maklumat Peribadi</CardTitle>
            <CardDescription className="text-xs">Kemas kini nama dan maklumat hubungan anda.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={saveProfile} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Nama Penuh</Label>
                <Input data-testid="profile-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Emel</Label>
                <Input value={user?.email || ""} disabled className="bg-slate-50" data-testid="profile-email-input" />
                <p className="text-xs text-muted-foreground">Emel tidak boleh ditukar.</p>
              </div>
              <div className="space-y-1.5">
                <Label>No. Telefon</Label>
                <Input data-testid="profile-phone-input" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="012-3456789" />
              </div>
              <Button type="submit" className="bg-[#10056E] hover:bg-[#2a17a8]" disabled={busy} data-testid="profile-save-btn">Simpan Profil</Button>
            </form>
          </CardContent>
        </Card>

        {EMAIL_PREFS.some((p) => p.roles.includes(user?.role)) && (
        <Card className="border-slate-200 shadow-sm rounded-xl" data-testid="email-prefs-card">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2"><MailCheck className="h-4 w-4" /> Notifikasi Emel</CardTitle>
            <CardDescription className="text-xs">Pilih emel yang anda ingin terima daripada sistem.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1">
            {!prefs && <p className="text-sm text-muted-foreground">Memuatkan...</p>}
            {prefs && EMAIL_PREFS.filter((p) => p.roles.includes(user?.role)).map((p) => (
              <div key={p.key} className="flex items-center justify-between gap-4 border-b last:border-0 py-3" data-testid={`email-pref-row-${p.key}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{p.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{p.desc}</p>
                </div>
                <Switch checked={!!prefs[p.key]} onCheckedChange={(v) => togglePref(p.key, v)}
                  data-testid={`email-pref-toggle-${p.key}`} />
              </div>
            ))}
          </CardContent>
        </Card>
        )}

        <div className="space-y-6">
          {user?.role === "pemandu" && (
            <Card className="border-slate-200 shadow-sm rounded-xl" data-testid="license-card">
              <CardHeader>
                <CardTitle className="text-base font-semibold flex items-center gap-2"><IdCard className="h-4 w-4" /> Lesen Memandu</CardTitle>
                <CardDescription className="text-xs">Muat naik gambar lesen dan tarikh luput anda.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <input ref={licenseRef} type="file" accept="image/*" className="hidden" onChange={uploadLicense} data-testid="license-file-input" />
                {license.license_image ? (
                  <img src={license.license_image} alt="Lesen" className="w-full rounded-lg border" data-testid="license-preview" />
                ) : (
                  <p className="text-xs text-muted-foreground border border-dashed rounded-lg p-4 text-center">Tiada gambar lesen dimuat naik.</p>
                )}
                <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => licenseRef.current?.click()} data-testid="license-upload-btn">
                  <Upload className="h-3.5 w-3.5 mr-2" /> {license.license_image ? "Tukar Gambar Lesen" : "Muat Naik Gambar Lesen"}
                </Button>
                <div className="space-y-1.5">
                  <Label>Tarikh Luput Lesen</Label>
                  <div className="flex gap-2">
                    <Input type="date" value={license.license_expiry} onChange={(e) => setLicense({ ...license, license_expiry: e.target.value })} data-testid="license-expiry-input" />
                    <Button type="button" size="sm" className="bg-[#10056E] hover:bg-[#2a17a8] h-10" onClick={saveLicenseExpiry} data-testid="license-expiry-save-btn">Simpan</Button>
                  </div>
                  {license.license_expiry && new Date(license.license_expiry) < new Date() && (
                    <p className="text-xs font-semibold text-[#DC3545]" data-testid="license-expired-warning">Lesen anda telah tamat tempoh!</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          <Card className="border-slate-200 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2"><KeyRound className="h-4 w-4" /> Tukar Kata Laluan</CardTitle>
              <CardDescription className="text-xs">Pastikan kata laluan baharu sekurang-kurangnya 6 aksara.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={savePassword} className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Kata Laluan Semasa</Label>
                  <Input data-testid="password-current-input" type="password" required value={pwd.current_password}
                    onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Kata Laluan Baharu</Label>
                  <Input data-testid="password-new-input" type="password" required minLength={6} value={pwd.new_password}
                    onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Sahkan Kata Laluan Baharu</Label>
                  <Input data-testid="password-confirm-input" type="password" required minLength={6} value={pwd.confirm}
                    onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} />
                </div>
                <Button type="submit" className="bg-[#10056E] hover:bg-[#2a17a8]" disabled={busy} data-testid="password-save-btn">Tukar Kata Laluan</Button>
              </form>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2"><Building2 className="h-4 w-4" /> Agensi</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="flex justify-between"><span className="text-muted-foreground">Nama Agensi</span><span className="font-semibold">{tenant?.name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Peranan</span><span className="font-semibold">{ROLE_LABEL[user?.role]}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Ahli Sejak</span><span className="font-semibold">{fmtDate(user?.created_at)}</span></div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
